import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../Modal';
import { API } from '../../API';
import { projectsApi } from '../../utils/projectsApi';
import CasePicker from './CasePicker';
import type { CaseOption } from './CasePicker';
import ProjectCasesList from './ProjectCasesList';
import styles from './CreateProjectModal.module.scss';

interface Project {
  id: number;
  name: string;
  case_ids?: number[];
  order?: number;
}

interface InitialProject {
  id: number;
  name: string;
  case_ids?: number[];
}

interface CreateProjectModalProps {
  isVisible: boolean;
  onClose: () => void;
  onSuccess: (project: Project) => void;
  initialProject?: InitialProject | null;
}

const PAGE_SIZE = 50;

// initialProject — when provided the modal operates in edit mode
export default function CreateProjectModal({
  isVisible,
  onClose,
  onSuccess,
  initialProject,
}: CreateProjectModalProps) {
  const { t } = useTranslation();
  const isEditMode = Boolean(initialProject);

  const [name, setName] = useState('');
  const [selectedCases, setSelectedCases] = useState<CaseOption[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const editPageRef = useRef(1);

  // Tracks a project that was created but whose case_ids update failed, so a
  // retry updates the same record instead of creating a duplicate.
  const pendingProjectRef = useRef<{ id: number } | null>(null);

  // Populate fields when opening in edit mode
  useEffect(() => {
    if (!isVisible) return;

    setError(null);
    editPageRef.current = 1;

    if (isEditMode && initialProject) {
      setName(initialProject.name ?? '');
      setSelectedCases([]);
      setIsLoadingMore(true);

      let cancelled = false;
      const params = new URLSearchParams({
        size: String(PAGE_SIZE),
        page: '1',
        is_threat: 'false',
        project_id: String(initialProject.id),
      });

      API.fetch('GET', `/API/v1/narratives?${params}`)
        .then((data: any) => {
          if (cancelled) return;
          const objects = data?.objects ?? [];
          setSelectedCases(objects.map((n: any) => ({ label: n.name as string, value: n.id as number })));
          setHasMore(objects.length === PAGE_SIZE);
          setIsLoadingMore(false);
        })
        .catch(() => {
          if (!cancelled) {
            setSelectedCases([]);
            setIsLoadingMore(false);
          }
        });

      return () => { cancelled = true; };
    } else {
      setName('');
      setSelectedCases([]);
      setHasMore(false);
    }
  }, [isVisible, isEditMode, initialProject]);

  const handleLoadMore = useCallback(async () => {
    if (!initialProject || isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    const nextPage = editPageRef.current + 1;

    try {
      const params = new URLSearchParams({
        size: String(PAGE_SIZE),
        page: String(nextPage),
        is_threat: 'false',
        project_id: String(initialProject.id),
      });
      const data = await API.fetch('GET', `/API/v1/narratives?${params}`);
      const objects = data?.objects ?? [];
      setSelectedCases((prev) => [
        ...prev,
        ...objects.map((n: any) => ({ label: n.name as string, value: n.id as number })),
      ]);
      editPageRef.current = nextPage;
      setHasMore(objects.length === PAGE_SIZE);
    } catch {
      // silently fail — scrolling again will retry
    } finally {
      setIsLoadingMore(false);
    }
  }, [initialProject, isLoadingMore, hasMore]);

  const handleClose = useCallback(() => {
    setName('');
    setSelectedCases([]);
    setError(null);
    setHasMore(false);
    pendingProjectRef.current = null;
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    if (isSubmitting || !name.trim()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      let project: Project;

      if (isEditMode && initialProject) {
        const result = await projectsApi.update(initialProject.id, {
          name: name.trim(),
          case_ids: selectedCases.map((c) => c.value),
        });
        project = result ?? {
          ...initialProject,
          name: name.trim(),
          case_ids: selectedCases.map((c) => c.value),
        };
      } else {
        const case_ids = selectedCases.map((c) => c.value);

        if (pendingProjectRef.current) {
          const result = await projectsApi.update(pendingProjectRef.current.id, {
            name: name.trim(),
            case_ids,
          });
          project = result ?? { id: pendingProjectRef.current.id, name: name.trim(), case_ids };
        } else {
          const created = await projectsApi.create(name.trim());
          pendingProjectRef.current = { id: created.id };
          if (case_ids.length > 0) {
            await projectsApi.update(created.id, { case_ids });
          }
          project = { ...created, case_ids };
        }

        pendingProjectRef.current = null;
      }

      onSuccess(project);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsSubmitting(false);
    }
  }, [name, selectedCases, isEditMode, initialProject, onSuccess, handleClose, isSubmitting]);

  const selectedIds = new Set(selectedCases.map((c) => c.value));

  const content = (
    <div className={styles.body}>
      <div className={styles.field}>
        <label htmlFor="project-name" className={styles.label}>
          {t('Project name')}
          <span className={styles.required} aria-hidden="true">*</span>
        </label>
        <input
          id="project-name"
          type="text"
          className={styles.input}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && name.trim() && !isSubmitting) handleSubmit();
          }}
          placeholder={t('Enter project name')}
          autoFocus
          aria-required="true"
        />
      </div>

      <div className={styles.field}>
        <div className={styles.labelContainer}>
          <span className={styles.label}>{t('Cases')}</span>
          <CasePicker
            selectedIds={selectedIds}
            onAdd={(opt) => setSelectedCases((prev) => [...prev, opt])}
          />
        </div>
        

        <ProjectCasesList
          cases={selectedCases}
          onRemove={(id) => setSelectedCases((prev) => prev.filter((c) => c.value !== id))}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          onLoadMore={handleLoadMore}
        />
      </div>

      {/* <ProjectCasesList
        cases={selectedCases}
        onRemove={(id) => setSelectedCases((prev) => prev.filter((c) => c.value !== id))}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        onLoadMore={handleLoadMore}
      /> */}

      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
    </div>
  );

  const footer = (
    <div className={styles.footer}>
      <button
        type="button"
        className="btn-reset"
        onClick={handleClose}
        disabled={isSubmitting}
      >
        {t('Cancel')}
      </button>
      <button
        type="button"
        className="btn-primary"
        onClick={handleSubmit}
        disabled={!name.trim() || isSubmitting}
      >
        {isSubmitting
          ? isEditMode ? t('Saving…') : t('Creating…')
          : isEditMode ? t('Save') : t('Create')}
      </button>
    </div>
  );

  return (
    <Modal
      isVisible={isVisible}
      title={isEditMode ? t('Edit project') : t('Create new project')}
      content={content}
      footer={footer}
      onClose={handleClose}
      doNotCloseOnOutsideClick={true}
      className={styles.modal}
    />
  );
}
